import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider';
import { Button, Dialog, EmptyState } from '../../../components/ui';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { ROUTES } from '../../../constants/routes';
import { userInformationAtom } from '../../../Services/_states';
import useAnalyticsSx from '../hooks/useAnalyticsSx';
import useAnalyticsPermissions from '../hooks/useAnalyticsPermissions';
import useAnalyticsSuperAdmin from '../hooks/useAnalyticsSuperAdmin';
import useDashboardPins from '../hooks/useDashboardPins';
import WelcomePanel from '../components/home/WelcomePanel';
import PinnedReportGrid from '../components/home/PinnedReportGrid';
import SavedReportsDialog from '../components/home/SavedReportsDialog';
import DashboardDateControl from '../components/home/DashboardDateControl';
import OrganisationPicker from '../components/home/OrganisationPicker';
import AnalyticsPageHeader from '../components/AnalyticsPageHeader';
import { ANALYTICS_COPY } from '../constants/analyticsCopy';
import { getOrganisationId } from '../../intervene/utils/organisationSelectUtils';
import { getAnalyticsOrganisationId } from '../utils/analyticsOrgScope';
import { analyticsService } from '../services/analyticsService';
import { exportReportToExcel } from '../utils/exportReportToExcel';
import {
  CUSTOM_DATE_PRESET,
  REPORT_DEFAULT_PRESET,
  activeRangeLabel,
  applyDateOverride,
  datePresetLabels,
} from '../utils/datePresets';
import {
  applyReportCardFilters,
  readReportCardFilters,
} from '../utils/reportCardFilters';

function getOrganisationStorageKey(userInfo) {
  const userId = userInfo?.id || userInfo?.username || userInfo?.email || 'default';
  return `analytics-selected-organisation:${userId}`;
}

function readSavedOrganisation(storageKey) {
  try {
    const saved = window.localStorage.getItem(storageKey);
    const organisation = saved ? JSON.parse(saved) : null;
    return getOrganisationId(organisation) ? organisation : null;
  } catch (error) {
    console.warn('Unable to restore the saved analytics organisation.', error);
    return null;
  }
}

function AnalyticsHomeContent() {
  const sx = useAnalyticsSx();
  const { semantic, typography } = useFeatureTheme();
  const navigate = useNavigate();
  const permissions = useAnalyticsPermissions();
  const isSuperAdmin = useAnalyticsSuperAdmin();
  const userInfo = useRecoilValue(userInformationAtom);

  const organisationStorageKey = useMemo(
    () => getOrganisationStorageKey(userInfo),
    [userInfo?.id, userInfo?.username, userInfo?.email],
  );
  const [organisation, setOrganisation] = useState(
    () => readSavedOrganisation(organisationStorageKey),
  );

  useEffect(() => {
    setOrganisation(readSavedOrganisation(organisationStorageKey));
  }, [organisationStorageKey]);

  const handleOrganisationChange = (nextOrganisation) => {
    setOrganisation(nextOrganisation);
    try {
      if (getOrganisationId(nextOrganisation)) {
        window.localStorage.setItem(
          organisationStorageKey,
          JSON.stringify({
            organisation_id: getOrganisationId(nextOrganisation),
            name: nextOrganisation.name || nextOrganisation.label || '',
          }),
        );
      } else {
        window.localStorage.removeItem(organisationStorageKey);
      }
    } catch (error) {
      console.warn('Unable to save the selected analytics organisation.', error);
    }
  };

  const orgId = useMemo(() => {
    if (isSuperAdmin) return getOrganisationId(organisation);
    return getAnalyticsOrganisationId(userInfo);
  }, [isSuperAdmin, organisation, userInfo]);

  const {
    pinned,
    saved,
    isLoading,
    deleteReport,
    reorderPins,
    resizePin,
    pinReport,
  } = useDashboardPins(orgId);
  const { showSnackBar } = useSnackBar();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [arranging, setArranging] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [dateView, setDateView] = useState({ preset: REPORT_DEFAULT_PRESET });
  const [rangePreviews, setRangePreviews] = useState(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [cardFilters, setCardFilters] = useState({});
  const [cardChartTypes, setCardChartTypes] = useState({});
  const [sortOrder, setSortOrder] = useState('');
  const rangeLabels = useMemo(() => datePresetLabels(ANALYTICS_COPY), []);

  const customRangeReady = dateView.preset !== CUSTOM_DATE_PRESET
    || (Boolean(dateView.start) && Boolean(dateView.end) && dateView.end >= dateView.start);

  useEffect(() => {
    const hasCardFilterOverrides = Object.keys(cardFilters).length > 0;
    if (
      !orgId
      || (dateView.preset === REPORT_DEFAULT_PRESET && !hasCardFilterOverrides)
      || !customRangeReady
    ) {
      setRangePreviews(null);
      setRangeLoading(false);
      return undefined;
    }
    let cancelled = false;
    setRangeLoading(true);
    const requested = pinned.filter((item) => item.report?.report_id);
    Promise.all(requested.map(async (item) => {
      const reportId = item.report.report_id;
      try {
        const selectedFilters = Object.prototype.hasOwnProperty.call(cardFilters, reportId)
          ? cardFilters[reportId]
          : readReportCardFilters(item.report.query_spec);
        const effectiveDateRange = selectedFilters?.dateRange?.preset && selectedFilters.dateRange.preset !== REPORT_DEFAULT_PRESET
          ? selectedFilters.dateRange
          : dateView;
        const res = await analyticsService.runQuery(
          applyReportCardFilters(
            applyDateOverride(item.report?.query_spec, effectiveDateRange),
            selectedFilters
          ),
          orgId,
        );
        return res.ok ? [reportId, res.data] : null;
      } catch {
        return null;
      }
    })).then((entries) => {
      if (cancelled) return;
      const loaded = entries.filter(Boolean);
      setRangePreviews(Object.fromEntries(loaded));
      setRangeLoading(false);
      if (loaded.length !== requested.length) {
        showSnackBar(ANALYTICS_COPY.dateRangeApplyFailed, 'error');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [cardFilters, customRangeReady, dateView, orgId, pinned, showSnackBar]);

  const hasPinned = pinned.length > 0;
  const showWelcome = !isLoading && !hasPinned;
  const needsOrg = isSuperAdmin && !orgId;

  const openReportEditor = (reportId) => {
    navigate(ROUTES.FEATURES.ANALYTICS_BUILDER_EDIT.replace(':reportId', reportId));
  };

  const canManageReports = permissions.canCreate;

  const handleReorder = async (reportIds) => {
    const res = await reorderPins(reportIds);
    if (!res.ok) showSnackBar(ANALYTICS_COPY.reorderReportsFailed, 'error');
  };

  const handleResize = async (reportId, size) => {
    const res = await resizePin(reportId, size);
    if (!res.ok) showSnackBar(ANALYTICS_COPY.reorderReportsFailed, 'error');
  };

  const handlePin = async (reportId) => {
    const res = await pinReport(reportId);
    if (!res.ok) showSnackBar(ANALYTICS_COPY.pinReportFailed, 'error');
  };

  const handleApplyCardFilters = (reportId, filters) => {
    setCardFilters((current) => ({ ...current, [reportId]: filters }));
    setRangePreviews({});
    setRangeLoading(true);
  };

  const handleChangeCardChartType = (reportId, chartType) => {
    setCardChartTypes((current) => ({ ...current, [reportId]: chartType }));
  };

  const handleExport = async (querySpec, result, reportName) => {
    try {
      await exportReportToExcel({
        reportName: reportName || 'analytics_report',
        result,
        querySpec,
        orgId,
      });
    } catch {
      showSnackBar(ANALYTICS_COPY.exportFailed, 'error');
    }
  };

  const pinnedIds = useMemo(
    () => new Set(pinned.map((item) => item.report?.report_id).filter(Boolean)),
    [pinned],
  );

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const res = await deleteReport(deleteTarget);
    setIsDeleting(false);
    if (res.ok) {
      showSnackBar(ANALYTICS_COPY.deleteReportSuccess, 'success');
      setDeleteTarget(null);
      return;
    }
    const message = typeof res.data === 'string'
      ? res.data
      : res.data?.general_error || ANALYTICS_COPY.deleteReportFailed;
    showSnackBar(message, 'error');
  };

  return (
    <Box sx={sx.pageRoot}>
      <AnalyticsPageHeader
        title={ANALYTICS_COPY.moduleTitle}
        subtitle={ANALYTICS_COPY.homeSubtitle}
        action={(
          <Stack direction="row" spacing={1} alignItems="center">
            {!needsOrg ? (
              <Button variant="outline" onClick={() => setReportsOpen(true)}>
                {ANALYTICS_COPY.reports}
              </Button>
            ) : null}
            {hasPinned ? (
              <Button
                variant={arranging ? 'primary' : 'outline'}
                onClick={() => setArranging((current) => !current)}
              >
                {arranging ? ANALYTICS_COPY.doneArranging : ANALYTICS_COPY.arrangeReports}
              </Button>
            ) : null}
            <Button variant="primary" onClick={() => navigate(ROUTES.FEATURES.ANALYTICS_BUILDER)}>
              {ANALYTICS_COPY.createReport}
            </Button>
          </Stack>
        )}
      />

      {isSuperAdmin ? (
        <OrganisationPicker organisation={organisation} onChange={handleOrganisationChange} sx={sx} />
      ) : null}

      {needsOrg ? (
        <EmptyState
          title={ANALYTICS_COPY.selectOrganisation}
          description={ANALYTICS_COPY.selectOrganisationHint}
        />
      ) : isLoading ? (
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      ) : showWelcome ? (
        <WelcomePanel
          onCreateReport={() => navigate(ROUTES.FEATURES.ANALYTICS_BUILDER)}
          onSelectSource={(datasetId) => {
            const params = new URLSearchParams({ dataset: datasetId });
            navigate(`${ROUTES.FEATURES.ANALYTICS_BUILDER}?${params.toString()}`);
          }}
        />
      ) : (
        <Box>
          <DashboardDateControl
            value={dateView}
            onChange={setDateView}
            sortOrder={sortOrder}
            onSortOrderChange={setSortOrder}
          />
          {rangeLoading ? (
            <Typography
              sx={{
                mb: 1.5,
                fontSize: typography.fontSizeMd,
                color: semantic.text.secondary,
              }}
            >
              {ANALYTICS_COPY.previewLoading}
            </Typography>
          ) : null}
          {arranging ? (
            <Typography
              sx={{
                mb: 1.5,
                fontSize: typography.fontSizeMd,
                color: semantic.text.secondary,
              }}
            >
              {ANALYTICS_COPY.arrangingHint}
            </Typography>
          ) : null}
          <PinnedReportGrid
            items={pinned.map((item) => {
              const reportId = item.report?.report_id;
              const hasRangePreview = rangePreviews
                && Object.prototype.hasOwnProperty.call(rangePreviews, reportId);
              const preview = hasRangePreview ? rangePreviews[reportId] : item.preview;
              const selectedFilters = Object.prototype.hasOwnProperty.call(cardFilters, reportId)
                ? cardFilters[reportId]
                : readReportCardFilters(item.report?.query_spec);
              const effectiveDateRange = selectedFilters?.dateRange?.preset && selectedFilters.dateRange.preset !== REPORT_DEFAULT_PRESET
                ? selectedFilters.dateRange
                : dateView;
              return {
                ...item,
                preview,
                rangeLabel: activeRangeLabel(item.report?.query_spec?.date_range, effectiveDateRange, rangeLabels),
                querySpec: applyReportCardFilters(
                  applyDateOverride(item.report?.query_spec, effectiveDateRange),
                  selectedFilters
                ),
                cardFilters: selectedFilters,
              };
            })}
            sx={sx}
            arranging={arranging}
            canManage={canManageReports}
            canDelete={permissions.canDelete}
            onEditReport={openReportEditor}
            onDeleteReport={setDeleteTarget}
            onReorder={handleReorder}
            onResize={handleResize}
            onApplyFilters={handleApplyCardFilters}
            chartTypes={cardChartTypes}
            onChangeChartType={handleChangeCardChartType}
            sortOrder={sortOrder}
            canExport={permissions.canExport}
            orgId={orgId}
            onExport={handleExport}
          />
        </Box>
      )}
      <SavedReportsDialog
        open={reportsOpen}
        onClose={() => setReportsOpen(false)}
        reports={saved}
        pinnedIds={pinnedIds}
        onOpen={(reportId) => {
          setReportsOpen(false);
          navigate(ROUTES.FEATURES.ANALYTICS_REPORT.replace(':reportId', reportId));
        }}
        onPin={handlePin}
      />
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        title={ANALYTICS_COPY.deleteReportTitle}
        maxWidth="xs"
        actions={(
          <>
            <Button
              variant="outlined"
              disabled={isDeleting}
              onClick={() => setDeleteTarget(null)}
            >
              {ANALYTICS_COPY.deleteReportCancel}
            </Button>
            <Button
              variant="destructive"
              loading={isDeleting}
              onClick={handleConfirmDelete}
            >
              {ANALYTICS_COPY.deleteReportConfirm}
            </Button>
          </>
        )}
      >
        {ANALYTICS_COPY.deleteReportMessage}
      </Dialog>
    </Box>
  );
}

export default function AnalyticsHomePage() {
  return (
    <FeatureThemeProvider feature="analytics">
      <AnalyticsHomeContent />
    </FeatureThemeProvider>
  );
}
